// Client-side implementations of the Workflow Doctor agent tools tagged
// `location: 'client'` on the server. Each one takes the LIVE local
// { workflowState, executionLog } and returns a JSON-serializable result.
//
// Wiring: when POST /workflow-doctor-chat/chat (or /chat/resume) responds
// with `{ kind: 'pending_client_tool', pendingClientTool: { name, args } }`,
// the popup looks up `clientToolMap[name]`, runs it, then posts the result
// back via /chat/resume.
//
// Source of truth is always the live (possibly unsaved) canvas state — same
// as AskCode. No fetch / DB calls in here.

// ---------------------------------------------------------------------------
// Helpers (module-private)
// ---------------------------------------------------------------------------

function connFrom(c) { return String(c.from || c.sourceNodeId || ''); }
function connTo(c)   { return String(c.to   || c.targetNodeId || ''); }

function getNodes(state) {
  return Array.isArray(state && state.nodes) ? state.nodes : [];
}

function getConnectionsArr(state) {
  return Array.isArray(state && state.connections) ? state.connections : [];
}

function getVariablesArr(state) {
  return Array.isArray(state && state.variables) ? state.variables : [];
}

function getLogEntries(executionLog) {
  if (!executionLog) return [];
  return Array.isArray(executionLog) ? executionLog : (Array.isArray(executionLog.entries) ? executionLog.entries : []);
}

var MAX_OUTPUT_CHARS = 4000;
function trimOutput(value) {
  var text = '';
  try { text = typeof value === 'string' ? value : JSON.stringify(value, null, 2); }
  catch (e) { text = String(value); }
  if (text.length > MAX_OUTPUT_CHARS) text = text.slice(0, MAX_OUTPUT_CHARS) + '\n... (trimmed)';
  return text;
}

function escapeRegex(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// ---------------------------------------------------------------------------
// Tool implementations
// ---------------------------------------------------------------------------

function listNodes(_args, ctx) {
  return getNodes(ctx.workflowState).map(function(n) {
    return {
      id: String(n.id || ''),
      title: String(n.title || ''),
      icon: String(n.icon || ''),
      category: String(n.category || ''),
      nodeType: String(n.nodeType || '')
    };
  });
}

// Compress changeHistory entries to {ts,prompt} so the Doctor sees how the
// node's stated goal evolved without dragging multi-KB diff blobs through
// the chat context. Cap at the most recent 5 — the latest one is the most
// authoritative "ground truth" for what the node is supposed to do.
var MAX_HISTORY_ENTRIES = 5;
var MAX_HISTORY_PROMPT_CHARS = 400;
function trimHistoryEntry(e) {
  if (!e || typeof e !== 'object') return null;
  var promptText = String(e.prompt || e.generationPrompt || e.askcodePrompt || '');
  if (promptText.length > MAX_HISTORY_PROMPT_CHARS) {
    promptText = promptText.slice(0, MAX_HISTORY_PROMPT_CHARS) + '... (trimmed)';
  }
  return {
    ts: e.ts || e.timestamp || e.at || null,
    prompt: promptText
  };
}
function trimChangeHistory(history) {
  if (!Array.isArray(history) || history.length === 0) return null;
  var slice = history.slice(-MAX_HISTORY_ENTRIES);
  var out = [];
  for (var i = 0; i < slice.length; i++) {
    var e = trimHistoryEntry(slice[i]);
    if (e && e.prompt) out.push(e);
  }
  return out.length > 0 ? out : null;
}

// Scan a custom-node code body for the three input sources a node can read:
//   - settings/params (built from node.hyperparameters)
//   - vars            (workflow variables on the canvas)
//   - inputs          (data flowing in from connected upstream nodes)
// Returns the keys referenced by each source. Used by buildUsageHints below
// to surface mismatches like "code reads settings.URL but no URL hyperparameter
// is defined" — a deterministic bug class the Doctor was previously missing.
function extractKeys(code, accessor) {
  var out = {};
  if (!code) return [];
  var safe = escapeRegex(accessor);
  // accessor["X"] / accessor['X'] / accessor.X
  var re = new RegExp('(?:^|[^A-Za-z0-9_$])' + safe + '\\s*(?:\\[\\s*[\'"]([^\'"]+)[\'"]\\s*\\]|\\.\\s*([A-Za-z_$][A-Za-z0-9_$]*))', 'g');
  var m;
  while ((m = re.exec(code)) !== null) {
    var key = m[1] || m[2];
    if (key) out[key] = true;
  }
  return Object.keys(out);
}

// Detect numeric inputs[N] / argsByName.X / args[N] usage in code.
function extractInputUsage(code) {
  if (!code) return { positional: [], named: [], readsBareInput: false };
  var positional = {};
  var named = {};
  var readsBareInput = /(?:^|[^A-Za-z0-9_$])input\b(?!s)/.test(code);
  var rePos = /(?:^|[^A-Za-z0-9_$])(?:inputs|args)\s*\[\s*(\d+)\s*\]/g;
  var reNamed = /(?:^|[^A-Za-z0-9_$])argsByName\s*(?:\[\s*[\'"]([^\'"]+)[\'"]\s*\]|\.\s*([A-Za-z_$][A-Za-z0-9_$]*))/g;
  var m;
  while ((m = rePos.exec(code)) !== null) positional[m[1]] = true;
  while ((m = reNamed.exec(code)) !== null) {
    var k = m[1] || m[2];
    if (k) named[k] = true;
  }
  return {
    positional: Object.keys(positional).map(Number).sort(function(a, b) { return a - b; }),
    named: Object.keys(named),
    readsBareInput: readsBareInput
  };
}

function diff(a, b) {
  var inB = {};
  for (var i = 0; i < b.length; i++) inB[b[i]] = true;
  var out = [];
  for (var j = 0; j < a.length; j++) if (!inB[a[j]]) out.push(a[j]);
  return out;
}

// Loose key match — strips non-alphanumerics and lowercases so "Bearer Token",
// "BearerToken", "bearer_token", and "BEARER-TOKEN" all hash to the same value.
function normalizeKey(key) {
  return String(key || '').replace(/[^A-Za-z0-9]/g, '').toLowerCase();
}

function findFuzzyMatch(target, candidates) {
  var norm = normalizeKey(target);
  if (!norm) return null;
  for (var i = 0; i < candidates.length; i++) {
    if (normalizeKey(candidates[i]) === norm) return candidates[i];
  }
  return null;
}

// A workflow variable counts as "available to read" if ANY of these is true:
//   1. It's declared in the canvas Variables panel (workflowState.variables[]).
//   2. A set-variable node writes it (paramValues.variable === "X").
//   3. Any custom node's code writes it via `vars["X"] = ...` / `vars.X = ...`.
// Without (2) and (3) the analyzer hallucinates "vars[X] is never declared"
// every time the user wires the value through a Store-X set-variable node or
// writes it from custom code — which is the planner's preferred pattern.
var CODE_WRITE_REGEX = /(?:^|[^A-Za-z0-9_$])vars\s*(?:\.\s*([A-Za-z_$][A-Za-z0-9_$]*)|\[\s*['"]([^'"]+)['"]\s*\])\s*(?:\+|-|\*|\/|%|\|\||&&|\?\?)?=(?!=)/g;

function collectVarWritesFromCode(code) {
  if (typeof code !== 'string' || code.indexOf('vars') < 0) return [];
  var out = [];
  var seen = {};
  var re = new RegExp(CODE_WRITE_REGEX.source, 'g');
  var m;
  while ((m = re.exec(code)) !== null) {
    var name = m[1] || m[2];
    if (name && !seen[name]) { seen[name] = true; out.push(name); }
  }
  return out;
}

function collectAvailableVarNames(workflowState) {
  var names = {};
  var add = function(raw) {
    var n = String(raw || '').trim();
    if (n) names[n] = true;
  };
  var vars = getVariablesArr(workflowState);
  for (var v = 0; v < vars.length; v++) {
    var vn = vars[v];
    add((vn && vn.name) || (vn && vn.paramValues && vn.paramValues.name));
  }
  var nodes = getNodes(workflowState);
  for (var i = 0; i < nodes.length; i++) {
    var n = nodes[i];
    if (!n) continue;
    var icon = String(n.icon || n.type || '').toLowerCase();
    if (icon === 'set-variable') {
      add(n.paramValues && n.paramValues.variable);
    }
    var writes = collectVarWritesFromCode(String(n.code || ''));
    for (var w = 0; w < writes.length; w++) add(writes[w]);
  }
  return Object.keys(names);
}

// Given a key the code is trying to read from one source (settings or vars),
// find which OTHER source actually contains that value on the canvas. Returns
// the most likely correct read expression, e.g. `vars["TopResultUrl"]` or
// `inputs[0]`. Returns null if no plausible source exists — the doctor should
// then tell the user to add the missing hyperparameter / variable / connection.
//
// Preference order when several alternatives exist: vars > settings > inputs.
// vars are stable across graph rewires; inputs[0] silently breaks the moment
// the user adds, removes, or reorders an upstream connection. Always steer
// the doctor toward the variable when one is available.
function suggestCorrectSource(opts) {
  // opts: { key, missingFrom: 'settings' | 'vars',
  //         hyperKeys, canvasVarNames, hasIncoming }
  if (opts.missingFrom !== 'vars') {
    var varMatch = findFuzzyMatch(opts.key, opts.canvasVarNames);
    if (varMatch) return { source: 'vars', expr: 'vars["' + varMatch + '"]', matchedName: varMatch };
  }
  if (opts.missingFrom !== 'settings') {
    var hyperMatch = findFuzzyMatch(opts.key, opts.hyperKeys);
    if (hyperMatch) return { source: 'settings', expr: 'settings["' + hyperMatch + '"]', matchedName: hyperMatch };
  }
  if (opts.hasIncoming) {
    return { source: 'inputs', expr: 'inputs[0]', matchedName: null };
  }
  return null;
}

// Format a mismatch into a one-liner that includes BOTH the bug and the fix
// the Doctor should propose. Keeps the prompt-side instructions consistent
// across all mismatch kinds.
function formatMismatchDetail(opts) {
  // opts: { wrongExpr, suggestion }
  var base = 'code reads ' + opts.wrongExpr + ' which is undefined at runtime';
  if (!opts.suggestion) {
    return base + ' — no matching value found on the canvas. Tell the user to either define it on this node (add the hyperparameter), declare it in the Variables panel, or wire an incoming connection that delivers it.';
  }
  return base + '. The value the user wired is in a different source — change the code to ' + opts.suggestion.expr + '.';
}

// Cross-reference what the node's code reads against what's actually defined
// on the node (hyperparameters) and on the canvas (workflow variables). The
// resulting `mismatches` array is the headline signal — each entry is a real,
// runtime-undefined read the Doctor should flag, with a `suggestedFix` that
// points at the source where the value actually lives.
function buildUsageHints(node, workflowState) {
  var code = String((node && node.code) || '');
  if (!code.trim()) return null;
  var settingsKeysRead = extractKeys(code, 'settings').concat(extractKeys(code, 'params'));
  // De-dupe across the two aliases.
  var seen = {};
  settingsKeysRead = settingsKeysRead.filter(function(k) {
    if (seen[k]) return false;
    seen[k] = true;
    return true;
  });
  var varsRead = extractKeys(code, 'vars');
  var inputUsage = extractInputUsage(code);
  var hyperKeys = (Array.isArray(node && node.hyperparameters) ? node.hyperparameters : [])
    .map(function(h) { return String((h && h.key) || ''); })
    .filter(Boolean);
  // canvasVarNames is the union of: declared Variables-panel names,
  // set-variable node targets, and custom-code `vars["X"] = ...` writes.
  // Anything in this set is safe to read via vars[...] at runtime.
  var canvasVarNames = collectAvailableVarNames(workflowState);
  // Incoming connections matter when we suggest inputs[0] as a fallback.
  var nodeId = String((node && node.id) || '');
  var conns = getConnectionsArr(workflowState);
  var hasIncoming = false;
  for (var ci = 0; ci < conns.length; ci++) {
    if (connTo(conns[ci]) === nodeId) { hasIncoming = true; break; }
  }

  var mismatches = [];
  diff(settingsKeysRead, hyperKeys).forEach(function(k) {
    var suggestion = suggestCorrectSource({
      key: k,
      missingFrom: 'settings',
      hyperKeys: hyperKeys,
      canvasVarNames: canvasVarNames,
      hasIncoming: hasIncoming
    });
    mismatches.push({
      kind: 'settings_key_undefined',
      key: k,
      wrongExpr: 'settings["' + k + '"]',
      suggestedFix: suggestion,
      detail: formatMismatchDetail({ wrongExpr: 'settings["' + k + '"]', suggestion: suggestion })
    });
  });
  diff(varsRead, canvasVarNames).forEach(function(k) {
    var suggestion = suggestCorrectSource({
      key: k,
      missingFrom: 'vars',
      hyperKeys: hyperKeys,
      canvasVarNames: canvasVarNames,
      hasIncoming: hasIncoming
    });
    mismatches.push({
      kind: 'var_undeclared',
      key: k,
      wrongExpr: 'vars["' + k + '"]',
      suggestedFix: suggestion,
      detail: formatMismatchDetail({ wrongExpr: 'vars["' + k + '"]', suggestion: suggestion })
    });
  });

  return {
    settingsKeysReadByCode: settingsKeysRead,
    varsReadByCode: varsRead,
    inputsReadByCode: inputUsage,
    definedHyperparameterKeys: hyperKeys,
    canvasVariableNames: canvasVarNames,
    hasIncomingConnections: hasIncoming,
    mismatches: mismatches
  };
}

function getNode(args, ctx) {
  if (!args || !args.nodeId) return { error: 'nodeId required' };
  var nodes = getNodes(ctx.workflowState);
  var target = null;
  for (var i = 0; i < nodes.length; i++) {
    if (String(nodes[i] && nodes[i].id) === String(args.nodeId)) { target = nodes[i]; break; }
  }
  if (!target) return { error: 'Node not found: ' + args.nodeId };
  // generationPrompt + changeHistory are the GROUND TRUTH for what the node
  // is meant to do. The Doctor compares actual code/paramValues against this
  // intent — drift between intent and implementation is a real bug class.
  return {
    id: target.id,
    title: target.title || '',
    icon: target.icon || '',
    category: target.category || '',
    nodeType: target.nodeType || '',
    code: target.code || null,
    paramValues: target.paramValues || null,
    hyperparameters: target.hyperparameters || null,
    conditionRule: target.conditionRule || null,
    generationPrompt: target.generationPrompt || '',
    changeHistory: trimChangeHistory(target.changeHistory),
    usageHints: buildUsageHints(target, ctx.workflowState)
  };
}

function getConnections(args, ctx) {
  if (!args || !args.nodeId) return { error: 'nodeId required' };
  var conns = getConnectionsArr(ctx.workflowState);
  var id = String(args.nodeId);
  var incoming = [];
  var outgoing = [];
  for (var i = 0; i < conns.length; i++) {
    var c = conns[i];
    if (!c) continue;
    var entry = {
      id: c.id,
      from: connFrom(c),
      to: connTo(c),
      fromAnchor: c.fromAnchor || c.sourceAnchor || '',
      toAnchor: c.toAnchor || c.targetAnchor || '',
      propPath: c.propPath || ''
    };
    if (entry.to === id) incoming.push(entry);
    if (entry.from === id) outgoing.push(entry);
  }
  return { incoming: incoming, outgoing: outgoing };
}

function getVariables(_args, ctx) {
  return getVariablesArr(ctx.workflowState).map(function(v) {
    return {
      name: v.name || (v.paramValues && v.paramValues.name) || '',
      type: v.type || (v.paramValues && v.paramValues.dataType) || '',
      value: v.value !== undefined ? v.value : (v.paramValues && v.paramValues.value),
      defaultValue: v.defaultValue
    };
  });
}

function getNodeOutputFromLog(args, ctx) {
  if (!args || !args.nodeId) return { error: 'nodeId required' };
  var entries = getLogEntries(ctx.executionLog);
  var id = String(args.nodeId);
  for (var i = entries.length - 1; i >= 0; i--) {
    var e = entries[i];
    if (!e) continue;
    var entryNodeId = String(e.nodeId || (e.node && e.node.id) || '');
    if (entryNodeId === id) {
      return {
        found: true,
        status: e.status || e.state || '',
        output: trimOutput(e.output !== undefined ? e.output : e.result),
        error: e.error ? String(e.error) : '',
        durationMs: e.durationMs || e.duration || null
      };
    }
  }
  return { found: false };
}

function listExecutionLog(args, ctx) {
  var entries = getLogEntries(ctx.executionLog);
  var limit = (args && Number(args.limit)) > 0 ? Number(args.limit) : 20;
  var out = [];
  for (var i = entries.length - 1; i >= 0 && out.length < limit; i--) {
    var e = entries[i];
    if (!e) continue;
    out.push({
      nodeId: String(e.nodeId || (e.node && e.node.id) || ''),
      title: String(e.nodeTitle || (e.node && e.node.title) || ''),
      status: e.status || e.state || '',
      durationMs: e.durationMs || e.duration || null,
      hasError: !!e.error
    });
  }
  return { count: out.length, entries: out };
}

function getExecutionErrors(_args, ctx) {
  return getLogEntries(ctx.executionLog).filter(function(e) {
    if (!e) return false;
    if (e.error) return true;
    var s = String(e.status || e.state || '').toLowerCase();
    return s === 'error' || s === 'failed' || s === 'failure';
  }).map(function(e) {
    return {
      nodeId: String(e.nodeId || (e.node && e.node.id) || ''),
      title: String(e.nodeTitle || (e.node && e.node.title) || ''),
      status: e.status || e.state || 'error',
      error: String(e.error || '')
    };
  });
}

// runWorkflow — async, side-effecty.
//
// Flow:
//   1. Ask the user via the confirmation chip (ctx.requestRunWorkflowConfirm).
//      Resolves to true (run) / false (skip).
//   2. On run, kick off ctx.runWorkflow() which clicks the Play button and
//      resolves once execution reaches a terminal status.
//   3. Summarize the resulting log into errors[] + outputs{} keyed by nodeId.
//
// All three ctx fns are wired by DoctorChatPopup → doctorChat.sendMessage.
// If any are missing we return an error so the agent doesn't silently hang.
var RUN_OUTPUT_CHARS_PER_NODE = 800;
function trimNodeOutput(value) {
  var text = '';
  try { text = typeof value === 'string' ? value : JSON.stringify(value); }
  catch (e) { text = String(value); }
  if (text.length > RUN_OUTPUT_CHARS_PER_NODE) {
    text = text.slice(0, RUN_OUTPUT_CHARS_PER_NODE) + '... (trimmed)';
  }
  return text;
}

function summarizeRunResult(result) {
  var entries = getLogEntries(result && result.executionLog);
  var errors = [];
  var outputs = {};
  for (var i = 0; i < entries.length; i++) {
    var e = entries[i];
    if (!e) continue;
    var nid = String(e.nodeId || (e.node && e.node.id) || '');
    if (!nid) continue;
    var status = String(e.status || e.state || '').toLowerCase();
    if (e.error || status === 'error' || status === 'failed' || status === 'failure') {
      errors.push({
        nodeId: nid,
        title: String(e.nodeTitle || (e.node && e.node.title) || ''),
        error: String(e.error || 'failed without an error message')
      });
    }
    if (e.output !== undefined || e.result !== undefined) {
      outputs[nid] = trimNodeOutput(e.output !== undefined ? e.output : e.result);
    }
  }
  return {
    status: result && result.status ? String(result.status) : 'unknown',
    durationMs: result && result.durationMs ? result.durationMs : null,
    errorCount: errors.length,
    nodeCount: Object.keys(outputs).length,
    errors: errors,
    outputs: outputs
  };
}

async function runWorkflow(_args, ctx) {
  if (typeof ctx.requestRunWorkflowConfirm !== 'function' || typeof ctx.runWorkflow !== 'function') {
    return { error: 'runWorkflow is not wired up in this view' };
  }
  var confirmed;
  try {
    confirmed = await ctx.requestRunWorkflowConfirm();
  } catch (err) {
    return { error: 'confirmation prompt failed: ' + ((err && err.message) || String(err)) };
  }
  if (!confirmed) {
    return { skipped: true, reason: 'user declined' };
  }
  var runResult;
  try {
    runResult = await ctx.runWorkflow();
  } catch (err) {
    return { error: 'execution failed to start: ' + ((err && err.message) || String(err)) };
  }
  if (!runResult) {
    return { error: 'runWorkflow returned no result' };
  }
  return summarizeRunResult(runResult);
}

function inspectVariableUsage(args, ctx) {
  var name = String((args && args.variableName) || '').trim();
  if (!name) return { error: 'variableName required' };
  var nodes = getNodes(ctx.workflowState);
  var safe = escapeRegex(name);
  // Reads from custom code: vars["X"] / vars['X'] / vars.X.
  var readsCode = new RegExp('(?:^|[^A-Za-z0-9_$])vars\\s*(\\.\\s*' + safe + '\\b|\\[\\s*[\'"]' + safe + '[\'"]\\s*\\])');
  // Writes from custom code: vars["X"] = / vars.X = (single =, not ==/===).
  // Allows compound-assign forms (+= -= *= /= %= ||= &&= ??=).
  var writesCode = new RegExp('(?:^|[^A-Za-z0-9_$])vars\\s*(\\.\\s*' + safe + '\\b|\\[\\s*[\'"]' + safe + '[\'"]\\s*\\])\\s*(?:\\+|-|\\*|\\/|%|\\|\\||&&|\\?\\?)?=(?!=)');
  // Reads from paramValues: @X token (used by templated prompts, conditions).
  var readsParam = new RegExp('@' + safe + '\\b');
  var readers = [];
  var writers = [];
  // The Variables-panel default is a first-class runtime source: the engine
  // seeds the variable map from the declaring node's pv.value BEFORE any node
  // runs. A variable with a non-empty default is defined at runtime even with
  // zero set-variable/code writers — so the doctor must not call it "unset".
  var declared = false;
  var defaultValue;
  for (var i = 0; i < nodes.length; i++) {
    var n = nodes[i];
    if (!n) continue;
    var pv = n.paramValues || {};
    var codeStr = String(n.code || '');
    // Declaration — the Variables-panel node holding this variable's default.
    if (n.icon === 'variables' && String(n.title || '').trim() === name) {
      declared = true;
      defaultValue = pv.value;
    }
    // Writers — set-variable node OR custom code with vars["X"] = ...
    var isSetVarWriter = n.icon === 'set-variable' && String(pv.variable || '') === name;
    var isCodeWriter = writesCode.test(codeStr);
    if (isSetVarWriter || isCodeWriter) {
      writers.push({
        id: n.id,
        title: n.title || '',
        // Tag the source so the doctor can tell the user "Format Slack
        // Message writes ThemeSummary in custom code" vs "Set Email Content
        // is a set-variable node". Helpful when the model is composing a
        // fix plan and needs to know whether to edit code or wire a node.
        via: isSetVarWriter ? 'set-variable' : 'custom-code'
      });
    }
    // Readers — @X in any paramValue OR vars["X"]/vars.X in custom code.
    var paramsStr = '';
    try { paramsStr = JSON.stringify(pv); } catch (e) { paramsStr = ''; }
    var isCodeReader = readsCode.test(codeStr);
    var isParamReader = readsParam.test(paramsStr);
    if (isCodeReader || isParamReader) {
      readers.push({
        id: n.id,
        title: n.title || '',
        via: isCodeReader ? (isParamReader ? 'custom-code+param' : 'custom-code') : 'param'
      });
    }
  }
  return {
    readers: readers,
    writers: writers,
    declared: declared,
    defaultValue: defaultValue,
    // True when the panel default holds a real value (not '', null, or
    // whitespace). When true, readers>0 + writers=0 is NORMAL, not a bug.
    hasNonEmptyDefault: String(defaultValue == null ? '' : defaultValue).trim().length > 0
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

var clientToolMap = {
  listNodes: listNodes,
  getNode: getNode,
  getConnections: getConnections,
  getVariables: getVariables,
  getNodeOutputFromLog: getNodeOutputFromLog,
  listExecutionLog: listExecutionLog,
  getExecutionErrors: getExecutionErrors,
  inspectVariableUsage: inspectVariableUsage,
  runWorkflow: runWorkflow
};

// Async-aware: existing tools return plain objects, runWorkflow returns a
// Promise. Awaiting both works because await on a non-promise is a no-op.
async function runClientTool(name, args, ctx) {
  var fn = clientToolMap[name];
  if (typeof fn !== 'function') return { error: 'Unknown client tool: ' + name };
  try { return await fn(args || {}, ctx || {}); }
  catch (err) { return { error: (err && err.message) || String(err) }; }
}

module.exports = {
  clientToolMap: clientToolMap,
  runClientTool: runClientTool
};
